/**
 * Shared, framework-agnostic presentation logic for the podium (verdict).
 *
 * Both the deck `PodiumSlide` (packages/deck-ui) and the report `PodiumCard`
 * (apps/frontend report) consume these helpers so the two surfaces cannot
 * drift on score formatting or rank-badge semantics. Pure functions only — no
 * JSX, no styling — each surface owns its own markup.
 *
 * Score contract: `scoreUnit` on the payload declares the figure. Shares
 * render with `%`; composite indices keep `scoreLabel` and never a global
 * percent suffix. Equal-rank groups are computed from unrounded source
 * results, never from the displayed rounded string.
 */

import { roundHalfAwayFromZero, roundHalfAwayFromZeroAt } from '../foundations/producer-rounding';

export type PodiumScoreUnit = "percent" | "index";

function isFiniteScore(score?: number | null): score is number {
  return score !== null && score !== undefined && Number.isFinite(score);
}

// The producer's one rounding rule, half away from zero (Python's
// `figure_round`, this package's `producer-rounding`).
function roundToDecimals(score: number, decimals: number): number {
  return roundHalfAwayFromZeroAt(score, decimals);
}

/** Score string at `decimals` places, or null when there is no finite score. */
export function formatPodiumScore(
  score?: number | null,
  decimals = 0,
): string | null {
  if (!isFiniteScore(score)) {
    return null;
  }
  if (decimals <= 0) {
    return String(roundHalfAwayFromZero(score));
  }
  return roundToDecimals(score, decimals).toFixed(decimals);
}

/**
 * A share at the board's `decimals`, printed as the choice bars print one:
 * a trailing zero dropped ("24%", "24.1%"). Python stamps a pick share's
 * board at the share places, so the podium, the ranking bars and the prose
 * beside them spell one figure once.
 */
export function formatPodiumShare(
  score?: number | null,
  decimals = 0,
): string | null {
  const formatted = formatPodiumScore(score, decimals);
  if (formatted === null) {
    return null;
  }
  // `Number` drops the trailing zeros of the already-rounded figure.
  return `${Number(formatted)}%`;
}

/**
 * Extra display digits needed so unequal source scores do not look tied.
 *
 * Integer rounding is the default. When two different source results would
 * collapse to the same rounded string, keep enough precision to separate them.
 * True ties (identical source values) stay identical at every precision.
 */
export function podiumDisplayDecimals(
  scores: Array<number | null | undefined>,
): number {
  const unique: Array<number> = [];
  scores.forEach((score) => {
    if (!isFiniteScore(score)) {
      return;
    }
    if (unique.some((value) => value === score)) {
      return;
    }
    unique.push(score);
  });
  for (let decimals = 0; decimals <= 3; decimals += 1) {
    const rounded = unique.map((score) => roundToDecimals(score, decimals));
    if (new Set(rounded).size === unique.length) {
      return decimals;
    }
  }
  return 3;
}

/**
 * The full score line. Shares append `%`; indices keep the label and never a
 * global percent suffix. `decimals` is the explanatory precision for the board.
 */
export function formatPodiumScoreWithLabel(
  score?: number | null,
  scoreLabel?: string | null,
  unit?: PodiumScoreUnit | null,
  decimals = 0,
): string | null {
  const withUnit = unit === "percent" ? formatPodiumShare(score, decimals) : formatPodiumScore(score, decimals);
  if (withUnit === null) {
    return null;
  }
  const label = scoreLabel?.trim();
  return label ? `${withUnit} ${label}` : withUnit;
}

/**
 * Dense ranks from unrounded source scores, in payload order.
 *
 * Equal stored scores share a rank; the next distinct score takes the next
 * integer. Missing scores trail the finite groups without inventing a tie.
 */
export function podiumDenseRanks(
  scores: Array<number | null | undefined>,
): Array<number> {
  const finite: Array<{ index: number; score: number }> = [];
  const missing: Array<number> = [];
  scores.forEach((score, index) => {
    if (!isFiniteScore(score)) {
      missing.push(index);
      return;
    }
    finite.push({ index, score });
  });

  const uniqueDesc: Array<number> = [];
  finite
    .slice()
    .sort((left, right) => right.score - left.score)
    .forEach(({ score }) => {
      if (uniqueDesc.some((value) => value === score)) {
        return;
      }
      uniqueDesc.push(score);
    });
  const rankByScore = new Map(
    uniqueDesc.map((score, order) => [score, order + 1]),
  );

  const ranks: Array<number> = new Array(scores.length).fill(0);
  finite.forEach(({ index, score }) => {
    ranks[index] = rankByScore.get(score) ?? 1;
  });
  let nextRank = uniqueDesc.length + 1;
  missing.forEach((index) => {
    ranks[index] = nextRank;
    nextRank += 1;
  });
  return ranks;
}
